import { FC, ReactNode } from 'react';

import Field from './Field';

export type ColorFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  description?: string;
  /** Same contract as Field: shown instead of the description. */
  error?: ReactNode;
  id?: string;
  /** Grid placement: take the whole row of a FormGrid. */
  span?: 'full';
  className?: string;
};

export type ColorInputProps = {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  className?: string;
};

/**
 * A native colour picker in the shared control shell.
 *
 * The wrapper, not the input, draws the height, border and focus so the colour
 * control reads like every other control. The hex is repeated beside it because
 * a native swatch shows no value.
 */
export const ColorInput: FC<ColorInputProps> = ({ value, onChange, id, className, ...rest }) => (
  // A label, not a span: the native input is only a 20px swatch, so the shell
  // would draw a 40-44px box that does not itself open the picker. A label
  // forwards the click to its control, which makes the whole shell the target.
  // Field renders its own label as a sibling, so this is not a nested label.
  <label className={['ui-color-input', className].filter(Boolean).join(' ')}>
    <input id={id} type="color" value={value} onChange={(event) => onChange(event.target.value)} {...rest} />
    {/* aria-hidden: the wrapping label contributes its text to the input's
        accessible name, and the Field's own label already names the control. */}
    <code aria-hidden="true">{value.toUpperCase()}</code>
  </label>
);

/** A labelled colour control. */
const ColorField: FC<ColorFieldProps> = ({ label, value, onChange, description, error, id, span, className }) => (
  <Field label={label} id={id} description={description} error={error} span={span} className={className}>
    <ColorInput value={value} onChange={onChange} />
  </Field>
);

export default ColorField;
